import { Loading } from "../app/Status";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";
import {
  MENTION_DIRS,
  statusLabel,
  TASK_STATUS_LABELS,
  TASK_STATUSES,
  type MentionDir,
  type MyTaskDto,
  type TaskStatus,
} from "@klantpunt/shared";
import { Tabs } from "../app/Choice";
import { MentionsList, useMentions } from "../features/logbook/Mentions";
import { fmtDue } from "../features/tasks/TaskBoard";
import { myTasksKey, useMyTasks } from "../features/tasks/useTasks";
import { api, ApiError } from "../lib/api";
import { hasModule, useMe } from "../lib/auth";
import { useClientBuildingFilter } from "../lib/clientContext";
import { useToast } from "../lib/toast";
import { PageHeader } from "../app/PageHeader";
import { useState } from "react";
import { BookDialog, needsBooking, TaskMaterialsList } from "../features/materials/TaskMaterials";

type Tab = "taken" | "vermeldingen";

/**
 * "Mijn taken": startscherm van de uitvoerder (SPEC §8), met het tabblad Vermeldingen: waar ik met @ genoemd
 * ben en waar ik iemand noemde (issue #154).
 */
export function MyTasksPage() {
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "vermeldingen" ? "vermeldingen" : "taken";
  const dirParam = params.get("van") as MentionDir | null;
  const dir: MentionDir = dirParam && MENTION_DIRS.includes(dirParam) ? dirParam : "alle";
  const setView = (t: Tab, d: MentionDir = "alle") =>
    setParams(t === "taken" ? {} : d === "alle" ? { tab: t } : { tab: t, van: d }, { replace: true });
  const mentions = useMentions();
  const q = useMyTasks();
  const qc = useQueryClient();
  const toast = useToast();

  const [book, setBook] = useState<{ task: MyTaskDto; finish: boolean } | null>(null);
  const setStatus = (t: MyTaskDto, status: TaskStatus) =>
    status === "klaar" && hasModule(me, "materials") && needsBooking(t)
      ? setBook({ task: t, finish: true })
      : patch(`/tasks/${t.id}`, { status });

  async function patch(path: string, body: object) {
    try {
      await api(path, { method: "PATCH", body });
      await qc.invalidateQueries({ queryKey: myTasksKey });
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Er ging iets mis");
    }
  }

  // Volgt de klantwisselaar.
  const ofClient = useClientBuildingFilter();
  const list = (q.data ?? []).filter((t) => ofClient(t.building.id));
  const header = (
    <PageHeader
      label="Mijn taken"
      title={me?.user.name ?? "Mijn taken"}
      tabs={
        <Tabs<Tab>
          label="Mijn taken"
          value={tab}
          onChange={(t) => setView(t)}
          items={[
            { value: "taken", label: "Taken", count: list.length || undefined },
            { value: "vermeldingen", label: "Vermeldingen", count: mentions.data?.unread || undefined },
          ]}
        />
      }
    />
  );
  if (tab === "vermeldingen")
    return (
      <div className="page">
        {header}
        <MentionsList dir={dir} onDir={(d) => setView("vermeldingen", d)} />
      </div>
    );
  return (
    <div className="page">
      {header}
      {q.isPending && <Loading />}
      {!q.isPending && list.length === 0 && (
        <div className="panel">
          <p className="muted">Je hebt geen taken.</p>
        </div>
      )}
      {list.map((t: MyTaskDto) => (
        <article className="panel task" key={t.id} aria-label={`Taak ${t.code}`}>
          <div className="h">
            <span className="code small">{t.code}</span>
            <strong className="ttl">{t.title}</strong>
          </div>
          <div className="hint">
            <Link to={`/gebouwen/${t.building.id}`}>
              {t.building.code} · {t.building.name}
            </Link>
            {t.dueDate && ` · uiterlijk ${fmtDue(t.dueDate)}`}
          </div>
          <label className="field inline">
            <span>Status taak</span>
            <select value={t.status} onChange={(e) => void setStatus(t, e.target.value as TaskStatus)}>
              {TASK_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {TASK_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
          <TaskMaterialsList task={t} onBook={() => setBook({ task: t, finish: false })} />
          <ul className="tissues">
            {t.issues.map((i) => (
              <li key={i.id}>
                <Link to={`/gebouwen/${t.building.id}?bevinding=${i.id}`} className="tissue">
                  <span className={`chip prio prio-${i.priority}`}>{i.priority}</span>
                  <span className="code small">{i.code}</span>
                  <span className="grow">{i.title}</span>
                  <span className={`chip st-${i.status}`}>{statusLabel(i.status)}</span>
                </Link>
                {i.status !== "opgelost" && i.status !== "gesloten" && (
                  <button
                    type="button"
                    className="btn small"
                    onClick={() => patch(`/issues/${i.id}`, { status: "opgelost" })}
                  >
                    Opgelost
                  </button>
                )}
              </li>
            ))}
            {t.issues.length === 0 && <li className="hint">Geen bevindingen gekoppeld.</li>}
          </ul>
        </article>
      ))}
      {book && (
        <BookDialog
          task={book.task}
          finish={
            book.finish
              ? async () => {
                  await api(`/tasks/${book.task.id}`, { method: "PATCH", body: { status: "klaar" } });
                }
              : undefined
          }
          onClose={() => setBook(null)}
          onDone={() => qc.invalidateQueries({ queryKey: myTasksKey })}
        />
      )}
    </div>
  );
}
